<template>
    <div>
        <Dropdown
            :id="selectId"
            filter
            v-model="selectedUuid"
            :options="contactOptions"
            optionValue="uuid"
            optionLabel="displayName"
            :placeholder="placeholder"
            :showClear="true"
            class="w-full mt-1"
        />

        <p
            v-if="unexpectedError"
            data-test="unexpected-error"
            class="mt-2 text-xs text-red-600"
        >
            {{ $t("errors.unexpected") }}
        </p>

        <div v-if="selectedContact" class="mt-2 text-xs text-minplan-darkbeige leading-5">
            <p v-if="resolvedEmail">
                {{ resolvedEmail }}
                <span
                    v-if="emailIsFallback"
                    data-test="email-fallback"
                    class="italic text-gray-400"
                >
                    ({{ $t("suppliers.contactFallbackNotice") }})
                </span>
            </p>
            <p v-if="resolvedPhone">
                {{ resolvedPhone }}
                <span
                    v-if="phoneIsFallback"
                    data-test="phone-fallback"
                    class="italic text-gray-400"
                >
                    ({{ $t("suppliers.contactFallbackNotice") }})
                </span>
            </p>
        </div>

        <!-- Der Auslöser bleibt bewusst UNTER dem Auswahlfeld statt (wie im
             Prototyp) im Fuss des Dropdown-Panels: gerade der häufigste Fall —
             ein Unternehmer ohne jede Kontaktperson — wäre dort erst nach dem
             Öffnen einer leeren Liste zu finden. Entscheid Luca, 2026-08-13. -->
        <div v-if="!showCreate" class="mt-2">
            <button
                type="button"
                class="text-sm text-minplan-blue hover:underline disabled:opacity-40 disabled:no-underline"
                @click="toggleCreate"
                :disabled="!supplier"
            >
                + {{ $t("suppliers.addContact") }}
            </button>
        </div>

        <div
            v-if="showCreate"
            class="mt-2 p-4 rounded-lg bg-minplan-beige/40 border border-minplan-darkbeige/30"
        >
            <p class="text-sm font-medium text-gray-700 mb-3">
                {{ $t("suppliers.newContact") }}
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label class="block text-xs font-medium text-gray-600">
                        {{ $t("suppliers.firstname") }} <RequiredMark />
                    </label>
                    <input
                        type="text"
                        v-model="newContact.firstname"
                        required
                        class="mt-1 block w-full text-sm border-gray-300 rounded shadow-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    />
                    <jet-input-error
                        :message="createErrors.firstname?.[0]"
                        class="mt-1"
                    />
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-600">
                        {{ $t("suppliers.lastname") }} <RequiredMark />
                    </label>
                    <input
                        type="text"
                        v-model="newContact.lastname"
                        required
                        class="mt-1 block w-full text-sm border-gray-300 rounded shadow-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    />
                    <jet-input-error :message="createErrors.lastname?.[0]" class="mt-1" />
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-600">{{
                        $t("suppliers.email")
                    }}</label>
                    <input
                        type="email"
                        v-model="newContact.email"
                        class="mt-1 block w-full text-sm border-gray-300 rounded shadow-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    />
                    <jet-input-error :message="createErrors.email?.[0]" class="mt-1" />
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-600">{{
                        $t("suppliers.phone")
                    }}</label>
                    <input
                        type="text"
                        v-model="newContact.phone"
                        class="mt-1 block w-full text-sm border-gray-300 rounded shadow-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    />
                    <jet-input-error :message="createErrors.phone?.[0]" class="mt-1" />
                </div>
            </div>

            <div class="mt-3 flex justify-end items-center gap-3">
                <!-- Auch das Abbrechen ist während des Requests gesperrt:
                     sonst schliesst der Benutzer die Karte, und die Antwort
                     landet danach ins Leere — ein 500 zeigte seine Meldung bei
                     geschlossener Karte, ein Erfolg selektierte still einen
                     Kontakt, den niemand mehr anlegen wollte. -->
                <button
                    type="button"
                    class="text-sm text-gray-500 hover:underline disabled:opacity-40 disabled:no-underline"
                    @click="toggleCreate"
                    :disabled="creating"
                >
                    {{ $t("actions.cancel") }}
                </button>
                <button
                    type="button"
                    class="text-sm text-white bg-minplan-blue rounded px-4 py-1.5 disabled:opacity-40"
                    @click="createContact"
                    :disabled="creating"
                >
                    {{ creating ? $t("actions.saving") : $t("actions.save") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import axios from "axios";
import RequiredMark from "@/Shared/Components/RequiredMark";
import JetInputError from "@/Jetstream/InputError";

/**
 * Thema 9 (Welle 4, Paket c) — Kontaktperson-Auswahl je Unternehmer.
 *
 * Props im Vue2-`v-model`-Stil (`value`/`input`, NICHT `modelValue`/
 * `update:modelValue`) — so vom Plan
 * (`docs/superpowers/plans/2026-08-05-welle4-thema9-unternehmer-kontakte.md`,
 * Paket c) vorgegeben.
 *
 * Inline-Anlage läuft per Axios (begründete Ausnahme, s. Plan): sie läuft
 * INNERHALB eines offenen Inertia-Modals mit halb ausgefülltem `useForm`-
 * Zustand — ein Inertia-Roundtrip würde die Props des Hosts neu laden und
 * den Formularzustand des Offer-/Themen-Modals verwerfen. Nach Erfolg wird
 * die Kontaktliste lokal ergänzt und der neue Kontakt selektiert, KEIN
 * Inertia-Reload.
 *
 * Auswahl über PrimeVue `Dropdown` (Fix-Runde 1, Nitpick 2) — Bestandsmuster
 * der Supplier-Auswahl daneben (`CreateOffer.vue` u. a., 17 Dateien mit
 * `optionLabel`). `contactOptions` synthetisiert `displayName` («Vorname
 * Nachname»), weil PrimeVues `optionLabel` nur einen flachen Schlüssel liest,
 * keinen zusammengesetzten Ausdruck.
 *
 * R3 Punkt 2 (BACKLOG §5 Welle-4 (h)): ein Namensschema für den Kontakt-
 * Fallback — `resolvedEmail`/`resolvedPhone` + `emailIsFallback`/
 * `phoneIsFallback`, dieselben Schlüssel wie `TopicShowResource
 * ::supplierContactBlock()` und `Pages/Suppliers/Show.vue
 * ::contactsWithFallback`. Die BERECHNUNG bleibt hier dennoch eine eigene
 * (bewusster, letzter verbleibender Duplikatpunkt): dieser Block deckt die
 * INLINE-VORSCHAU eines gerade erst angelegten Kontakts, für den es noch
 * keinen Server-Roundtrip gibt (`localContacts` wird bei `createContact()`
 * lokal ergänzt, kein Inertia-Reload) — anders als der Themen-Kontext, wo die
 * Fallback-Berechnung serverseitig läuft und das Frontend nur noch anzeigt.
 *
 * R3 Punkt 4 (BACKLOG §5 Welle-4 (j)): der Unternehmer-Wechsel-Reset (vorher
 * vier identische Watcher in `CreateOffer`/`UpdateOffer`/`ReplaceOffer`/
 * `Topics/Modals/Supplier`) wandert hierher — der Picker beobachtet seine
 * eigene `supplier`-Prop und emittiert bei einem ECHTEN Wechsel `input(null)`.
 * Kein `immediate`: der Watcher feuert nicht beim ersten Rendern, die
 * Vorbelegung aus Offerte/Thema (`value`-Prop) bleibt unangetastet. Der Reset
 * feuert ausserdem nur, wenn zuvor bereits ein Unternehmer gewählt war (nie
 * beim allerersten "kein Unternehmer" → "Unternehmer X") — sonst würde ein
 * Picker, der erst beim ersten Wählen überhaupt gemountet wird (Host-Template
 * zeigt ihn nur bei vorhandenem Unternehmer), fälschlich die frische
 * Vorbelegung verwerfen.
 *
 * Die vier Hosts binden den Picker seither mit `v-show` statt `v-if`
 * (weiterhin dieselbe UI-Sichtbarkeit): `v-if` würde die Komponente beim
 * Zurücksetzen auf "kein Unternehmer" zerstören, BEVOR der Watcher den
 * Übergang beobachten kann — der alte host-seitige Reset feuerte aber genau
 * auch für "Unternehmer → kein Unternehmer" (nicht nur "Unternehmer A →
 * B"). `v-show` hält die Instanz am Leben und bewahrt dieses Verhalten 1:1.
 *
 * R3 Punkt 5 (BACKLOG §5 Welle-4 (k)): zeigt `value` beim Erstellen auf einen
 * Kontakt, der nicht (mehr) in `contacts` steht (tote UUID, z. B. gelöschte
 * Kontaktperson), emittiert der Picker einmalig `input(null)` statt den toten
 * Wert weiterzusenden — sonst schlägt der Submit heute mit 422 fehl, obwohl
 * der Picker leer/inaktiv aussieht. Fix-Runde 1 (B1, PFLICHT): der Wächter
 * greift nur, wenn ZUSÄTZLICH ein Unternehmer aufgelöst ist — sonst hätte
 * ein wegen `v-show` auch ohne Unternehmer gemounteter Picker (inaktiver
 * Unternehmer, fehlt in `activeSuppliers`) eine gültige Vorbelegung mit
 * einer toten UUID verwechselt, s. Docblock bei `created()`.
 *
 * R3 Punkt 6 (BACKLOG §5 Welle-4 (l)): ein Fehler ausserhalb 422 (403/500)
 * lief bisher unbemerkt durch (`.catch` warf ihn nur weiter, keine UI). Jetzt
 * eine allgemeine Fehlermeldung im Modal plus Konsolen-Log.
 */
export default defineComponent({
    name: "ContactPicker",
    components: { RequiredMark, JetInputError },
    props: {
        supplier: {
            type: Object,
            default: null,
        },
        value: {
            type: String,
            default: null,
        },
    },
    emits: ["input"],
    data() {
        return {
            localContacts: this.supplier?.contacts ? [...this.supplier.contacts] : [],
            showCreate: false,
            creating: false,
            createErrors: {},
            unexpectedError: false,
            newContact: {
                firstname: "",
                lastname: "",
                email: "",
                phone: "",
            },
        };
    },
    computed: {
        contacts() {
            return this.localContacts;
        },
        // PrimeVue `optionLabel` liest nur einen flachen Schlüssel — die
        // Dropdown-Optionen bekommen darum ein synthetisiertes `displayName`
        // («Vorname Nachname») zusätzlich zu den Rohfeldern.
        contactOptions() {
            return this.contacts.map((contact) => ({
                ...contact,
                displayName: `${contact.firstname} ${contact.lastname}`.trim(),
            }));
        },
        selectedContact() {
            return this.contacts.find((contact) => contact.uuid === this.value) ?? null;
        },
        // `v-model` für den Dropdown: liest die `value`-Prop, schreibt über
        // `onSelect()` — dieselbe einzige Stelle, die auch der native Change-
        // Pfad vorher nutzte (Vue2-`v-model`-Konvention der Komponente selbst
        // bleibt unangetastet: Eltern binden weiterhin `:value`/`@input`).
        selectedUuid: {
            get() {
                return this.value;
            },
            set(uuid) {
                this.onSelect(uuid);
            },
        },
        resolvedEmail() {
            return this.selectedContact?.email || this.supplier?.email || "";
        },
        resolvedPhone() {
            return this.selectedContact?.phone || this.supplier?.phone || "";
        },
        emailIsFallback() {
            return Boolean(
                this.selectedContact &&
                    !this.selectedContact.email &&
                    this.supplier?.email
            );
        },
        phoneIsFallback() {
            return Boolean(
                this.selectedContact &&
                    !this.selectedContact.phone &&
                    this.supplier?.phone
            );
        },
        selectId() {
            return `contact-picker-${this.supplier?.uuid ?? "none"}`;
        },
        // Der Leerfall wird im Platzhalter beantwortet statt in einer zweiten
        // Zeile darunter — sonst stünde dieselbe Aussage doppelt, sobald die
        // Anlege-Karte offen ist.
        placeholder() {
            return this.contacts.length
                ? this.$t("suppliers.noContactSelected")
                : this.$t("suppliers.noContactsYetShort");
        },
    },
    watch: {
        // Ein Unternehmer-Wechsel im Host-Modal (`supplier`-Prop wechselt)
        // ersetzt die lokale Kontaktliste. Kein `immediate` — der Watcher
        // beobachtet nur ECHTE Änderungen NACH dem ersten Rendern, die
        // Vorbelegung des Hosts (Offerte/Thema) bleibt unangetastet.
        //
        // R3 Punkt 4: zusätzlich der Reset selbst (vorher vier identische
        // Host-Watcher) — nur wenn zuvor bereits ein Unternehmer gewählt war
        // UND sich die UUID tatsächlich ändert (das deckt auch den Fall
        // "Unternehmer → kein Unternehmer", s. Docblock oben zu `v-show`).
        supplier(newSupplier, oldSupplier) {
            this.localContacts = newSupplier?.contacts ? [...newSupplier.contacts] : [];

            const oldUuid = oldSupplier?.uuid ?? null;
            const newUuid = newSupplier?.uuid ?? null;
            if (oldUuid !== null && newUuid !== oldUuid) {
                this.onSelect(null);
            }
        },
    },
    created() {
        // R3 Punkt 5, Fix-Runde 1 (B1, PFLICHT): `value` kann beim Mounten
        // auf eine tote UUID zeigen (Kontaktperson zwischenzeitlich
        // gelöscht) — einmalig korrigieren, statt den toten Wert beim
        // Submit gegen eine 422 laufen zu lassen.
        //
        // Wächter `this.supplier &&` ist Pflicht, nicht Zierde: der Picker
        // ist wegen `v-show` (R3 Punkt 4) auch gemountet, wenn (noch) gar
        // kein Unternehmer aufgelöst ist — z. B. weil der zugeordnete
        // Unternehmer inaktiv gesetzt wurde und darum in `activeSuppliers`
        // fehlt, `selectedSupplier` also `null` liefert, obwohl das Thema/
        // die Offerte weiterhin einen gültigen `value` (Kontaktperson)
        // trägt. Ohne den Wächter wäre `this.supplier` `null`,
        // `this.contacts` leer, `selectedContact` folglich nie auflösbar —
        // der Picker hätte die bestehende Vorbelegung beim nächsten
        // Speichern still gelöscht, obwohl der Kontakt real noch existiert.
        if (this.supplier && this.value && !this.selectedContact) {
            this.onSelect(null);
        }
    },
    methods: {
        onSelect(uuid) {
            this.$emit("input", uuid || null);
        },
        toggleCreate() {
            this.showCreate = !this.showCreate;
            this.createErrors = {};
            this.unexpectedError = false;
        },
        createContact() {
            if (!this.supplier || this.creating) {
                return;
            }

            this.creating = true;
            this.createErrors = {};
            this.unexpectedError = false;

            axios
                .post(
                    this.route("advisors.suppliers.contacts.store", this.supplier.uuid),
                    this.newContact
                )
                .then((response) => {
                    const contact = response.data.contact;

                    this.localContacts.push(contact);
                    this.onSelect(contact.uuid);
                    this.newContact = {
                        firstname: "",
                        lastname: "",
                        email: "",
                        phone: "",
                    };
                    this.showCreate = false;
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.createErrors = error.response.data.errors || {};
                    } else {
                        // R3 Punkt 6: 403/500 etc. liefen bisher ohne
                        // Rückmeldung durch (nur Konsole).
                        console.error(error);
                        this.unexpectedError = true;
                    }
                })
                .finally(() => {
                    this.creating = false;
                });
        },
    },
});
</script>

<style scoped></style>
